import useFetchAndTrace from "../fetchWithTrace";
import settings from "../../../constants/settings";
import { useCallback, useEffect, useState } from "react";
import { StencilType } from "../../../reducers/uploadedBoard";
import { useRouter } from "next/router";
import _ from "lodash";
import { parseStateQuery } from "../commonly-stored/customerBoards";

export enum InventoryItemType {
  STENCIL = "Stencil",
  PANELS = "Panels",
  OTHER = "Other",
}

export interface StencilItemDetails {
  stencil_type: StencilType;
}

export interface PanelItemDetails {
  boards_per_panel: number;
}

export interface InventoryItemRequest {
  item_type: InventoryItemType;
  flashpcb_container_barcode: string;
  flashpcb_bin_barcode: string;
  nickname: string;
  last_counted_amount: number;
  notes: string;
  board_ids: string[] | null;
  stencil_item_details: StencilItemDetails | null;
  panel_item_details: PanelItemDetails | null;
}

export interface InventoryItem extends InventoryItemRequest {
  id: string;
  item_type: InventoryItemType;
  flashpcb_container_barcode: string;
  flashpcb_bin_barcode: string;
  nickname: string;
  checked_in_date: string;
  most_recent_update: string;
  last_counted_amount: number;
  notes: string;
  board_ids: string[] | null;
  stencil_item_details: StencilItemDetails | null;
  panel_item_details: PanelItemDetails | null;
}

export interface PaginatedInventoryItemsResponse {
  inventory_items: InventoryItem[];
  page: number;
  starting_index: number;
  items_per_page: number;
  total_count: number;
}

export default function useInventoryItems(
  onSuccess?: () => void,
  onError?: (detail: string) => void,
  itemType?: InventoryItemType,
) {
  const fetchAndTrace = useFetchAndTrace();
  const [loading, setLoading] = useState<boolean>(false);
  const [itemsResponse, setItemsResponse] =
    useState<PaginatedInventoryItemsResponse | null>(null);
  const router = useRouter();
  const page = parseInt(router.query.page as string) || 1;
  const stencilType = parseStateQuery<StencilType>(
    router.query.stencilType as string | undefined,
  );
  const flashpcbContainerBarcode =
    (router.query.flashpcbContainerBarcode as string) || null;

  const updateQueryParameters = useCallback(
    (
      newPage: number,
      newStencilType: string | null,
      newFlashpcbContainerBarcode: string | null,
    ) => {
      const query: {
        page: number;
        stencilType: string | null;
        flashpcbContainerBarcode: string | null;
      } = {
        page: newPage,
        stencilType: newStencilType,
        flashpcbContainerBarcode: newFlashpcbContainerBarcode,
      };
      void router.push(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { scroll: false },
      );
    },
    [router],
  );

  const getItemsHandler = useCallback(
    async (response: Response) => {
      if (response.ok) {
        const responseJSON =
          (await response.json()) as PaginatedInventoryItemsResponse;
        if (_.isEmpty(responseJSON.inventory_items) && page != 1) {
          updateQueryParameters(
            1,
            stencilType || null,
            flashpcbContainerBarcode,
          );
        }
        setItemsResponse(responseJSON);
        setLoading(false);
      } else {
        throw Error("Bad response");
      }
    },
    [stencilType, flashpcbContainerBarcode, page, updateQueryParameters],
  );

  const getItemsErrorHandler = useCallback(() => {
    setLoading(false);
  }, [setLoading]);

  const getPaginatedInventoryItems = useCallback(async () => {
    setLoading(true);
    await fetchAndTrace(
      `${settings.api_url}/inventory/items?item_type=${itemType || ""}&stencil_type=${stencilType || ""}&flashpcb_container_barcode=${flashpcbContainerBarcode || ""}&page=${page}`,
      getItemsHandler,
      getItemsErrorHandler,
    );
  }, [
    itemType,
    stencilType,
    flashpcbContainerBarcode,
    page,
    fetchAndTrace,
    getItemsHandler,
    getItemsErrorHandler,
  ]);

  useEffect(() => {
    if (itemType) {
      void getPaginatedInventoryItems();
    }
  }, [getPaginatedInventoryItems, itemType]);

  const errorHandler = () => {
    onError?.("Failed to update inventory item.");
    setLoading(false);
  };

  const updateInventoryItem = (
    inventoryItemUpdate: InventoryItemRequest,
    creatingNewItem: boolean,
  ) => {
    const updateInventoryItemHandler = async (response: Response) => {
      setLoading(false);
      if (response.ok) {
        onSuccess?.();
      } else if (response.status === 400 || response.status === 404) {
        const { detail } = (await response.json()) as { detail: string };
        onError?.(detail);
      } else {
        throw new Error("Failed to update inventory item");
      }
    };
    setLoading(true);
    void fetchAndTrace(
      `${settings.api_url}/inventory/items?creating_new_item=${creatingNewItem}`,
      updateInventoryItemHandler,
      errorHandler,
      {
        body: JSON.stringify(inventoryItemUpdate),
        method: "PUT",
      },
    );
  };

  const deleteInventoryItemHandler = async (response: Response) => {
    if (response.ok) {
      onSuccess?.();
    } else if (response.status === 400 || response.status === 404) {
      const { detail } = (await response.json()) as { detail: string };
      onError?.(detail);
    } else {
      throw new Error("Failed to delete inventory item(s).");
    }
  };
  const deleteInventoryItem = (itemIds: string[]) => {
    void fetchAndTrace(
      `${settings.api_url}/inventory/items/delete`,
      deleteInventoryItemHandler,
      errorHandler,
      {
        body: JSON.stringify({ item_ids_to_delete: itemIds }),
        method: "PUT",
      },
    );
  };

  return {
    updateInventoryItem,
    deleteInventoryItem,
    loading,
    getInventoryItems: getPaginatedInventoryItems,
    itemsResponse,
    updateQueryParameters,
    filterParameters: {
      page,
      stencilType,
      flashpcbContainerBarcode,
    },
  };
}
